import { Validation } from '@react-types/shared';
import React, {
  ForwardedRef,
  ReactElement,
  RefObject,
  useRef,
  useState,
} from 'react';
import {
  useRadioGroup,
  AriaRadioGroupProps,
  AriaRadioProps,
} from '@react-aria/radio';
import { useRadioGroupState } from '@react-stately/radio';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { SegmentedRadioContext } from './context';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/segmentedControl/segmentedControl.module.css';
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils';
import { useFocusVisible } from '@react-aria/interactions';
import useAnimatedSegment from '../../utils/useAnimatedSegment';
import { GlobalDomAttributes } from '../../utils/types';

export interface SegmentedRadioGroupProps
  extends Omit<
      AriaRadioGroupProps,
      | keyof Validation<unknown>
      | 'orientation'
      | 'label'
      | 'isReadOnly'
      | 'description'
      | 'errorMessage'
    >,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * The SegmentedRadio(s) contained within the SegmentedRadioGroup.
   */
  children: ReactElement<AriaRadioProps> | ReactElement<AriaRadioProps>[];
  /**
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg';
  'aria-label': string;

  /**
   * Alignment of the radio buttons group.
   * @default "left"
   */
  align?: 'left' | 'center' | 'right' | 'stretch';
}

export const SegmentedRadioGroup = React.forwardRef(function RadioGroup(
  props: SegmentedRadioGroupProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { children, size, align = 'left', ...otherProps } = props;
  const ref = useObjectRef(forwardedRef);
  const segmentedListRef = useRef<HTMLDivElement>(null);
  const [activeElement, setActiveElement] = useState<
    RefObject<HTMLInputElement>
  >({ current: null });
  const { isFocusVisible } = useFocusVisible();
  const { styleProps } = useSapphireStyleProps(otherProps);
  const state = useRadioGroupState(props);
  const { radioGroupProps } = useRadioGroup(props, state);
  const domProps = filterDOMProps(otherProps, { global: true });

  const { canAnimate, position } = useAnimatedSegment(
    {
      containerRef: segmentedListRef,
      activeElementRef: activeElement,
      onComputeRequest: (currentSegment: HTMLElement) => {
        const labelElement = currentSegment?.parentElement;
        return {
          offsetLeft: labelElement?.offsetLeft ?? 0,
          width: labelElement?.clientWidth ?? 0,
        };
      },
    },
    [state.selectedValue, align]
  );

  return (
    <div
      ref={ref}
      className={clsx(
        styles['sapphire-segmented-control'],
        styleProps.className,
        align === 'stretch'
          ? styles[`sapphire-segmented-control--stretch`]
          : [styles[`sapphire-segmented-control--align-${align}` as const]],
        {
          [styles['focus-visible']]: isFocusVisible,
          [styles['sapphire-segmented-control--lg']]: size === 'lg',
          [styles['sapphire-segmented-control--sm']]: size === 'sm',
        }
      )}
      style={styleProps.style}
    >
      <div
        ref={segmentedListRef}
        {...mergeProps(radioGroupProps, domProps, {
          className: styles['sapphire-segmented-control__button-container'],
        })}
      >
        <SegmentedRadioContext.Provider value={{ state, setActiveElement }}>
          {children}
        </SegmentedRadioContext.Provider>
        <span
          className={clsx(styles['sapphire-segmented-control__glider'], {
            [styles['sapphire-segmented-control__glider--with-transition']]:
              canAnimate,
          })}
          style={{
            width: position.width,
            left: position.offsetLeft,
          }}
        />
      </div>
    </div>
  );
});
